/// Collections de contenu suivies par la table d'audit `dataHistory` côté backend.
///
/// Doit rester synchronisée avec `ALLOWED_COLLECTIONS` dans
/// `pocketbase/pb_hooks/usecases/eventListener/saveUpdateHistory.js`.
export type HistoryCollection =
  | "topics"
  | "topicParts"
  | "topicPartQuestions"
  | "testimonies"
  | "trainingVersions";

/// Nature de l'événement d'historique.
///
/// `create` : création. `update` : modification. `delete` : suppression (l'enregistrement et sa
/// descendance). `restore` : restauration d'une version antérieure. `reorder` : changement de rang.
export type HistoryAction =
  | "create"
  | "update"
  | "delete"
  | "restore"
  | "reorder";

/// Auteur d'un événement.
///
/// `dataHistory.userId` est une relation vers `admins` : une simple jointure côté serveur suffit.
/// `isKnown` est faux pour une opération non attribuable — un superuser agissant depuis l'UI
/// PocketBase, ou un compte supprimé (la relation étant en `cascadeDelete: false`, la référence est
/// alors vidée). D'où l'intérêt de désactiver un compte (`isEnabled = false`) plutôt que de le
/// supprimer.
export interface HistoryAuthor {
  id: string;
  name: string;
  email: string;
  isEnabled: boolean;
  isKnown: boolean;
}

/// Libellé d'affichage d'un auteur, avec repli sur l'email puis sur l'anonymat.
export function authorLabel(
  author: HistoryAuthor,
  unknownLabel: string,
): string {
  if (!author.isKnown) return unknownLabel;
  return author.name || author.email || unknownLabel;
}

/// État complet d'un enregistrement à un instant donné, tel que sérialisé par le backend.
export type HistorySnapshot = Record<string, unknown>;

/// Collection désignée par un champ relation.
///
/// Plus large que [HistoryCollection] : un champ relation peut pointer vers une collection que
/// l'historique ne suit pas (`trainings`, `topicCategories`).
export type ReferencedCollection =
  | HistoryCollection
  | "trainings"
  | "topicCategories";

/// Identité d'un enregistrement désigné par son id dans un snapshot.
///
/// Résolue par le backend (`historyService.resolveRelationLabels`) pour **chaque** id rencontré
/// dans un champ relation, y compris irrésoluble ([label] vide) : la seule présence de la clé
/// permet alors de reconnaître une référence, sans dupliquer ici la table des champs relation.
export interface RecordLabel {
  label: string;
  collection: ReferencedCollection;
  /// L'enregistrement n'existe plus. [label] peut rester renseigné, lu dans son dernier snapshot.
  isDeleted: boolean;
}

/// Libellés des enregistrements référencés, indexés par `<champ>:<id>` (cf. [referenceKey]).
export type RecordLabels = Record<string, RecordLabel>;

/// Clé d'un libellé dans [RecordLabels].
///
/// Le champ fait partie de la clé : les enregistrements hérités portent des ids numériques courts
/// qui se recoupent d'une collection à l'autre (un même `27` est à la fois un `topics` et un
/// `topicParts`), et un id seul désignerait alors le mauvais élément.
export function referenceKey(field: string, id: string): string {
  return `${field}:${id}`;
}

/// Un événement de la timeline d'un enregistrement.
///
/// Le snapshot n'est pas transporté ici (ceux des `topicParts` contiennent de gros blocs HTML) :
/// seuls les noms des champs modifiés le sont. Le snapshot complet se récupère à la demande via
/// `getHistoryVersionUseCase`.
export interface HistoryVersion {
  id: string;
  created: string;
  action: HistoryAction;
  author: HistoryAuthor;
  changedFields: string[];
  /// L'événement fait **apparaître** le contenu : rien ne le précède (une création, ou une
  /// restauration qui recrée un enregistrement supprimé). Énumérer « tous les champs modifiés »
  /// n'aurait alors aucun sens : le contenu est présenté tel quel, sans colonne « avant ».
  appearance: boolean;
  hasSnapshot: boolean;
}

/// Réponse de la liste des versions : la timeline plus l'état courant, pour pouvoir comparer
/// n'importe quelle version à l'enregistrement vivant.
export interface HistoryTimeline {
  versions: HistoryVersion[];
  current: HistorySnapshot | null;
  /// Libellés des enregistrements référencés par [current].
  labels: RecordLabels;
}

/// Une version accompagnée de son snapshot complet.
export interface HistoryVersionDetail {
  id: string;
  collection: HistoryCollection;
  recordId: string;
  action: HistoryAction;
  created: string;
  author: HistoryAuthor;
  snapshot: HistorySnapshot | null;
  /// Libellés des enregistrements référencés par [snapshot].
  labels: RecordLabels;
}

/// État d'un élément supprimé : toujours absent, ou déjà ranimé par une restauration.
export type DeletedStatus = "deleted" | "restored";

/// Un élément lié supprimé, avec sa propre descendance supprimée.
export interface DeletedRecord {
  versionId: string;
  collection: HistoryCollection;
  recordId: string;
  label: string;
  /// `delete` : suppression tracée par le hook. `update` : orphelin déduit d'une ligne antérieure
  /// au hook — la date et l'auteur sont alors ceux de la dernière modification connue, pas de la
  /// suppression.
  action: HistoryAction;
  deletedAt: string;
  author: HistoryAuthor;
  status: DeletedStatus;
  /// Décidé par le serveur : seule la descendance **directe** de l'enregistrement consulté est
  /// restaurable individuellement. Vue depuis un thème, une question supprimée est un petit-enfant :
  /// elle revient avec sa partie, par la cascade de restauration.
  restorable: boolean;
  /// Renseignés pour un élément déjà restauré : l'id vivant qui le remplace, la date et l'auteur.
  restoredAs: string;
  restoredAt: string;
  restoredBy: HistoryAuthor | null;
  snapshot: HistorySnapshot | null;
  children: DeletedRecord[];
}

/// Résultat d'une restauration.
export interface RestoreResult {
  recordId: string;
  recreated: boolean;
  restoredFields: string[];
  /// Champs fichier non restaurés : PocketBase supprime le blob du storage lors d'une suppression
  /// ou d'un remplacement, l'ancienne image n'est donc plus récupérable.
  skippedFields: string[];
  /// Champs relation dont certaines références ont été retirées faute de cible existante
  /// (catégorie, sujets liés… supprimés depuis).
  prunedFields: string[];
  /// Éléments liés ranimés par la cascade : une partie restaurée ramène ses questions, supprimées
  /// avec elle.
  restoredChildren: number;
  /// Éléments liés que la cascade n'a pas pu ranimer (snapshot illisible, sauvegarde refusée).
  failedChildren: number;
}

/// Écart sur un champ entre deux états, pour l'affichage côte à côte.
export interface FieldDiff {
  field: string;
  before: unknown;
  after: unknown;
}

/// Champs techniques masqués dans les comparaisons.
///
/// Doit rester synchronisé avec `IGNORED_FIELDS` dans
/// `pocketbase/pb_hooks/services/historyService.js` : `updated_by` changeant à chaque modification,
/// il apparaîtrait sinon dans toutes les comparaisons alors que l'auteur est déjà porté par
/// l'événement.
const IGNORED_DIFF_FIELDS = [
  "id",
  "created",
  "updated",
  "collectionId",
  "collectionName",
  "expand",
  "created_by",
  "updated_by",
];

/// Champs qui diffèrent entre deux snapshots, triés par nom.
///
/// Un snapshot absent est traité comme vide : comparer une version à « rien » (enregistrement
/// supprimé) reste ainsi possible.
export function diffSnapshots(
  before: HistorySnapshot | null,
  after: HistorySnapshot | null,
): FieldDiff[] {
  const left = before ?? {};
  const right = after ?? {};

  const fields = new Set([...Object.keys(left), ...Object.keys(right)]);

  return [...fields]
    .filter((field) => !IGNORED_DIFF_FIELDS.includes(field))
    .filter(
      (field) => JSON.stringify(left[field]) !== JSON.stringify(right[field]),
    )
    .sort()
    .map((field) => ({ field, before: left[field], after: right[field] }));
}
